// Roadmap.jsx — Écran de l'ÉDITEUR (super admin) : les briques en
// construction, rangées dans la version qui les livrera.
//
// Une brique = un chantier. Sa couleur dit où il en est, sa largeur son
// ampleur. Une version = ce qui part chez les clients ; la publier passe la
// version ET toutes ses briques en « livrée », d'un bloc.
//
// Toutes les routes sont derrière super_admin côté serveur : le masquage de
// l'entrée de menu n'est pas la protection, le 403 l'est.
import React, { useCallback, useEffect, useState } from 'react';
import APIService from '../services/api';
import { confirmErpia } from './ConfirmDialog';
import '../roadmap.css';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const ETATS = [
  { cle: 'idee', label: 'Idée' },
  { cle: 'specifiee', label: 'Spécifiée' },
  { cle: 'dev', label: 'En développement' },
  { cle: 'test', label: 'En test' },
  { cle: 'livree', label: 'Livrée' },
];
const LIB_ETAT = Object.fromEntries(ETATS.map(e => [e.cle, e.label]));
const TAILLES = [
  { valeur: 2, label: 'Petite — quelques jours' },
  { valeur: 4, label: 'Moyenne — deux semaines' },
  { valeur: 6, label: 'Grande — un mois' },
];

const jour = iso => (iso ? String(iso).slice(0, 10).split('-').reverse().join('.') : '');
const libTrimestre = t => {
  if (!t) return '';
  const [an, q] = String(t).split('-T');
  return `${q === '1' ? '1er' : `${q}e`} trimestre ${an}`;
};
const libEcheance = v => (v.date_prevue ? `prévue le ${jour(v.date_prevue)}`
  : v.trimestre ? `prévue au ${libTrimestre(v.trimestre)}` : 'sans date pour l\'instant');

// Un appel, un contrat : { ok, data } ou { error } — le texte du serveur fait
// foi, il est affiché tel quel. absent = le module n'est pas encore déployé.
async function appel(chemin, options = {}) {
  let r;
  try {
    r = await fetch(`${BASE}/roadmap${chemin}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${APIService.getToken()}`, ...(options.headers || {}) },
    });
  } catch {
    return { error: 'Serveur injoignable — réessayez dans un instant.' };
  }
  if (r.status === 404 || r.status === 501) return { absent: true };
  let corps = null;
  try { corps = await r.json(); } catch { /* corps vide */ }
  if (!r.ok) return { error: corps?.error || `Erreur ${r.status}` };
  return { ok: true, data: corps?.data ?? corps };
}

export default function Roadmap({ showNotification }) {
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [absent, setAbsent] = useState(false);
  const [erreur, setErreur] = useState('');
  const [brique, setBrique] = useState(null);   // tiroir brique
  const [version, setVersion] = useState(null); // tiroir version
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    const out = await appel('');
    if (out.absent) { setAbsent(true); setData({ releases: [], sans_version: [] }); }
    else if (out.error) setErreur(out.error);
    else { setAbsent(false); setErreur(''); setData(out.data); }
    setChargement(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const enPreparation = (data?.releases || []).filter(r => r.etat !== 'publiee');
  const publiees = (data?.releases || []).filter(r => r.etat === 'publiee');

  // ── Écritures ─────────────────────────────────────────────────────────────
  const enregistrerBrique = async () => {
    const b = brique;
    if (!b?.titre?.trim()) { showNotification?.('Le titre de la brique est obligatoire.', 'error'); return; }
    setEnvoi(true);
    const corps = JSON.stringify({
      titre: b.titre, resume: b.resume, etat: b.etat, taille: b.taille,
      release_id: b.release_id === '' ? null : b.release_id,
    });
    const out = b.id
      ? await appel(`/briques/${b.id}`, { method: 'PUT', body: corps })
      : await appel('/briques', { method: 'POST', body: corps });
    setEnvoi(false);
    if (out.absent) { showNotification?.('La roadmap arrive côté serveur.', 'error'); return; }
    if (out.error) { showNotification?.(out.error, 'error'); return; }
    showNotification?.(b.id ? 'Brique enregistrée' : 'Brique ajoutée', 'success');
    setBrique(null);
    charger();
  };

  const supprimerBrique = async () => {
    if (!brique?.id) return;
    const ok = await confirmErpia({
      title: 'Supprimer la brique',
      message: `« ${brique.titre} » sera retirée de la roadmap.`,
      detail: 'Rien d\'autre n\'est touché. Cette suppression est définitive.',
      confirmLabel: 'Supprimer', tone: 'danger',
    });
    if (!ok) return;
    const out = await appel(`/briques/${brique.id}`, { method: 'DELETE' });
    if (out.error) { showNotification?.(out.error, 'error'); return; }
    showNotification?.('Brique supprimée', 'success');
    setBrique(null);
    charger();
  };

  const enregistrerVersion = async () => {
    const v = version;
    if (!v?.version?.trim()) { showNotification?.('Le numéro de version est obligatoire.', 'error'); return; }
    setEnvoi(true);
    const corps = JSON.stringify({
      version: v.version, nom: v.nom,
      echeance_mode: v.echeance_mode,
      date_prevue: v.echeance_mode === 'date' ? v.date_prevue : null,
      trimestre: v.echeance_mode === 'trimestre' ? v.trimestre : null,
    });
    const out = v.id
      ? await appel(`/releases/${v.id}`, { method: 'PUT', body: corps })
      : await appel('/releases', { method: 'POST', body: corps });
    setEnvoi(false);
    if (out.absent) { showNotification?.('La roadmap arrive côté serveur.', 'error'); return; }
    if (out.error) { showNotification?.(out.error, 'error'); return; }
    showNotification?.(v.id ? 'Version enregistrée' : 'Version créée', 'success');
    setVersion(null);
    charger();
  };

  const publier = async (r) => {
    const aLivrer = (r.briques || []).filter(b => b.etat !== 'livree');
    const pasPretes = aLivrer.filter(b => b.etat !== 'test');
    const ok = await confirmErpia({
      title: `Publier la version ${r.version}`,
      message: `${aLivrer.length} brique(s) passeront en « livrée ».`,
      detail: pasPretes.length
        ? `Dont ${pasPretes.length} qui ne sont pas encore en test : ${pasPretes.map(b => b.titre).join(', ')}.`
        : 'Toutes ses briques sont en test ou déjà livrées.',
      confirmLabel: 'Publier la version',
    });
    if (!ok) return;
    const out = await appel(`/releases/${r.id}/publier`, { method: 'POST' });
    if (out.error) { showNotification?.(out.error, 'error'); return; }
    showNotification?.(`Version ${r.version} publiée`, 'success');
    charger();
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  const Brique = ({ b }) => (
    <button className="rm-brique" data-etat={b.etat} style={{ '--rm-n': b.taille || 4 }}
      onClick={() => setBrique({ ...b, resume: b.resume || '', release_id: b.release_id ?? '' })}>
      <span className="rm-brique-etat">{LIB_ETAT[b.etat] || b.etat}</span>
      <span className="rm-brique-titre">{b.titre}</span>
      {b.resume && <span className="rm-brique-res">{b.resume}</span>}
    </button>
  );

  const nouvelleBrique = (releaseId) => setBrique({ titre: '', resume: '', etat: 'idee', taille: 4, release_id: releaseId ?? '' });

  if (chargement) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin" style={{ width: '40px', height: '40px', borderRadius: '50%', border: '2px solid var(--hair)', borderBottomColor: 'var(--accent)' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="rm-tete">
        <p className="rm-kicker">Éditeur · erpia</p>
        <h1>Roadmap<span className="rm-point">.</span></h1>
        <p className="rm-sous">Une brique par chantier, rangée dans la version qui la livrera. Sa largeur dit son ampleur, sa couleur où elle en est.</p>
      </div>

      {absent && (
        <div className="rm-bandeau">
          <BI icon="info-circle" />
          <span>La roadmap arrive côté serveur — l'écran est prêt, rien n'est enregistré pour l'instant.</span>
        </div>
      )}
      {erreur && (
        <div className="rm-bandeau rm-bandeau--erreur">
          <BI icon="exclamation-triangle" />
          <span>{erreur}</span>
        </div>
      )}

      {enPreparation.map(r => (
        <div className="rm-version" key={r.id}>
          <div className="rm-version-tete">
            <span className="rm-num">{r.version}</span>
            {r.nom && <span className="rm-nom">{r.nom}</span>}
            <button className="rm-echeance" type="button"
              onClick={() => setVersion({
                id: r.id, version: r.version, nom: r.nom || '',
                echeance_mode: r.date_prevue ? 'date' : r.trimestre ? 'trimestre' : 'aucune',
                date_prevue: r.date_prevue ? String(r.date_prevue).slice(0, 10) : '',
                trimestre: r.trimestre || '',
              })}>
              {libEcheance(r)}<span className="rm-modif">modifier</span>
            </button>
            <button className="erp-btn erp-btn--secondary erp-btn--sm" type="button" onClick={() => publier(r)} disabled={absent}>
              Publier la version
            </button>
          </div>
          <div className="rm-corps">
            <div className="rm-mur">
              {(r.briques || []).map(b => <Brique key={b.id} b={b} />)}
              <button className="rm-ajout" type="button" onClick={() => nouvelleBrique(r.id)} disabled={absent}>
                <BI icon="plus-lg" />Ajouter une brique
              </button>
            </div>
          </div>
        </div>
      ))}

      {!!(data?.sans_version || []).length && (
        <div className="rm-version">
          <div className="rm-version-tete"><span className="rm-nom">Sans version</span></div>
          <div className="rm-corps">
            <div className="rm-mur">
              {data.sans_version.map(b => <Brique key={b.id} b={b} />)}
            </div>
          </div>
        </div>
      )}

      <button className="rm-nouvelle" type="button" disabled={absent}
        onClick={() => setVersion({ version: '', nom: '', echeance_mode: 'date', date_prevue: '', trimestre: '2026-T4' })}>
        <BI icon="plus-lg" />Nouvelle version
      </button>

      {!!publiees.length && (
        <div className="rm-version">
          <div className="rm-version-tete"><span className="rm-nom">Déjà livré</span></div>
          <div className="rm-corps rm-livre">
            {publiees.map(r => (
              <button className="rm-mini" type="button" key={r.id}
                onClick={() => setVersion({
                  id: r.id, version: r.version, nom: r.nom || '',
                  echeance_mode: r.date_prevue ? 'date' : r.trimestre ? 'trimestre' : 'aucune',
                  date_prevue: r.date_prevue ? String(r.date_prevue).slice(0, 10) : '',
                  trimestre: r.trimestre || '',
                })}>
                <b>{r.version}</b>{r.nom && <span>{r.nom}</span>}<em>{jour(r.date_publication)}</em>
              </button>
            ))}
          </div>
        </div>
      )}

      {!enPreparation.length && !publiees.length && !(data?.sans_version || []).length && !absent && (
        <div className="rm-version"><p className="rm-vide">Rien encore. Créez une version, puis posez-y ce que vous construisez.</p></div>
      )}

      {/* ── Tiroir brique ── */}
      {brique && (<>
        <button className="rm-voile" type="button" aria-label="Fermer" onClick={() => setBrique(null)} />
        <aside className="rm-tiroir" aria-label="Brique">
          <div className="rm-tiroir-tete">
            <h2>{brique.id ? 'Modifier la brique' : 'Ajouter une brique'}</h2>
            <button className="erp-btn erp-btn--secondary erp-btn--sm" type="button" onClick={() => setBrique(null)} aria-label="Fermer"><BI icon="x-lg" /></button>
          </div>
          <div className="rm-tiroir-corps">
            <div className="rm-champ">
              <label htmlFor="rm-titre">Titre</label>
              <input className="erp-field" id="rm-titre" value={brique.titre} autoFocus
                placeholder="Export comptable Bexio"
                onChange={e => setBrique({ ...brique, titre: e.target.value })} />
            </div>
            <div className="rm-champ">
              <label htmlFor="rm-resume">En une phrase</label>
              <textarea className="erp-field" id="rm-resume" rows={3} value={brique.resume}
                placeholder="Un fichier d'écritures par mois pour la fiduciaire."
                onChange={e => setBrique({ ...brique, resume: e.target.value })} />
              <p className="rm-aide">C'est le texte que le client lira le jour de la livraison.</p>
            </div>
            <div className="rm-champ">
              <label>État</label>
              <div className="rm-segmente">
                {ETATS.map(e => (
                  <button key={e.cle} type="button" aria-pressed={brique.etat === e.cle}
                    onClick={() => setBrique({ ...brique, etat: e.cle })}>{e.label}</button>
                ))}
              </div>
            </div>
            <div className="rm-champ">
              <label>Taille</label>
              <div className="rm-segmente">
                {TAILLES.map(t => (
                  <button key={t.valeur} type="button" aria-pressed={Number(brique.taille) === t.valeur}
                    onClick={() => setBrique({ ...brique, taille: t.valeur })}>{t.label}</button>
                ))}
              </div>
              <p className="rm-aide">Sa largeur dans la version.</p>
            </div>
            <div className="rm-champ">
              <label htmlFor="rm-version">Version</label>
              <select className="erp-field" id="rm-version" value={brique.release_id ?? ''}
                onChange={e => setBrique({ ...brique, release_id: e.target.value === '' ? '' : Number(e.target.value) })}>
                <option value="">Sans version pour l'instant</option>
                {(data?.releases || []).map(r => (
                  <option key={r.id} value={r.id}>{r.version}{r.nom ? ` · ${r.nom}` : ''}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="rm-tiroir-pied">
            {brique.id
              ? <button className="erp-btn erp-btn--danger" type="button" onClick={supprimerBrique}>Supprimer</button>
              : <span />}
            <div className="rm-droite">
              <button className="erp-btn erp-btn--secondary" type="button" onClick={() => setBrique(null)}>Annuler</button>
              <button className="erp-btn erp-btn--primary" type="button" onClick={enregistrerBrique} disabled={envoi}>
                {envoi ? <BI icon="arrow-repeat" /> : null}{brique.id ? 'Enregistrer la brique' : 'Ajouter la brique'}
              </button>
            </div>
          </div>
        </aside>
      </>)}

      {/* ── Tiroir version ── */}
      {version && (<>
        <button className="rm-voile" type="button" aria-label="Fermer" onClick={() => setVersion(null)} />
        <aside className="rm-tiroir" aria-label="Version">
          <div className="rm-tiroir-tete">
            <h2>{version.id ? `Version ${version.version}` : 'Nouvelle version'}</h2>
            <button className="erp-btn erp-btn--secondary erp-btn--sm" type="button" onClick={() => setVersion(null)} aria-label="Fermer"><BI icon="x-lg" /></button>
          </div>
          <div className="rm-tiroir-corps">
            <div className="rm-champ">
              <label htmlFor="rm-num">Numéro</label>
              <input className="erp-field ats-mono" id="rm-num" value={version.version} autoFocus placeholder="1.9"
                onChange={e => setVersion({ ...version, version: e.target.value })} />
              <p className="rm-aide">Celui que le client lira le jour de la livraison.</p>
            </div>
            <div className="rm-champ">
              <label htmlFor="rm-nomv">Nom</label>
              <input className="erp-field" id="rm-nomv" value={version.nom} placeholder="Facturation"
                onChange={e => setVersion({ ...version, nom: e.target.value })} />
              <p className="rm-aide">Ce que cette version apporte, en un ou deux mots.</p>
            </div>
            <div className="rm-champ">
              <label>Échéance</label>
              <div className="rm-segmente">
                {[['date', 'Une date'], ['trimestre', 'Un trimestre'], ['aucune', 'Pas encore']].map(([cle, lib]) => (
                  <button key={cle} type="button" aria-pressed={version.echeance_mode === cle}
                    onClick={() => setVersion({ ...version, echeance_mode: cle })}>{lib}</button>
                ))}
              </div>
              {version.echeance_mode === 'date' && (<>
                <input className="erp-field" type="date" value={version.date_prevue}
                  onChange={e => setVersion({ ...version, date_prevue: e.target.value })} />
                <p className="rm-aide">Pour la version qu'on prépare : une date tenable, annoncée telle quelle.</p>
              </>)}
              {version.echeance_mode === 'trimestre' && (<>
                <select className="erp-field" value={version.trimestre || ''}
                  onChange={e => setVersion({ ...version, trimestre: e.target.value })}>
                  {['2026-T4', '2027-T1', '2027-T2', '2027-T3', '2027-T4'].map(t => (
                    <option key={t} value={t}>{libTrimestre(t)}</option>
                  ))}
                </select>
                <p className="rm-aide">Pour celles d'après : un trimestre engage moins qu'une date, et se tient.</p>
              </>)}
              {version.echeance_mode === 'aucune' && (
                <p className="rm-aide">La version existe et reçoit des briques, mais rien n'est annoncé. À dater quand la précédente est partie.</p>
              )}
            </div>
          </div>
          <div className="rm-tiroir-pied">
            <span />
            <div className="rm-droite">
              <button className="erp-btn erp-btn--secondary" type="button" onClick={() => setVersion(null)}>Annuler</button>
              <button className="erp-btn erp-btn--primary" type="button" onClick={enregistrerVersion} disabled={envoi}>
                {envoi ? <BI icon="arrow-repeat" /> : null}{version.id ? 'Enregistrer la version' : 'Créer la version'}
              </button>
            </div>
          </div>
        </aside>
      </>)}
    </div>
  );
}
